import LoadableStore, { LoadingState } from '@migrate/stores/LoadableStore'
import { action, makeObservable, override, runInAction } from 'mobx'
import { isNumber } from '@migrate/utils/common'
import { data, UserApi } from '@portal/pkg-api/shared'
import i18n from 'i18next'
import { DisplayableUser } from '@shared/model'

export default class UsersStore extends LoadableStore {
  public static STORE_NAME = 'usersStore'

  private usersInternal?: DisplayableUser[]

  constructor() {
    super()
    makeObservable(this)
  }

  public get users() {
    if (this.state !== LoadingState.LOADED) {
      throw new Error('Get users called before store loaded.')
    }
    return this.usersInternal!
  }

  @action
  public async load(): Promise<void> {
    this.state = LoadingState.LOADING
    await this.loadUsers()
    runInAction(() => {
      this.state = LoadingState.LOADED
    })
  }

  @override
  public async unload() {
    this.usersInternal = undefined
    await super.unload()
  }

  public findUserBy(id: number) {
    return this.users.find((u) => u.identifier === id.toString())
  }

  public findByDisplayName(displayName: string) {
    return this.users.find((u) => u.displayName === displayName)
  }

  public findByEmail(email: string) {
    return this.users.find((u) => u.email === email)
  }

  public findUserBestGuess(value: any) {
    let user
    if (isNumber(value)) {
      user = this.findUserBy(parseFloat(value as string))
    } else {
      user = this.findByEmail(value)
      if (!user) {
        user = this.findByDisplayName(value)
      }
    }
    return user
  }

  public findUserByIdOrShowValue(value: any) {
    // inline editor saves a user id
    if (isNumber(value)) {
      const u = this.findUserBy(parseInt(value as string, 10))
      return u ? u.displayName : ''
    }
    return value || ''
  }

  private async loadUsers() {
    const users = await data(UserApi.getUsers())
    this.usersInternal = users.map((u) => ({
      ...u,
      identifier: u.id?.toString(),
      displayName: i18n.t(u.active && u.enabled ? 'displayName' : 'displayName_inactive', {
        familyName: u.familyName,
        givenName: u.givenName,
      }),
    }))
  }
}
